/**
 * useAuthInit — initialization side-effects for the auth store.
 *
 * Must be mounted exactly once at the app root (e.g. inside <AppContent>).
 * Responsibilities (migrated from the former AuthContext):
 *   1. Restore the session on mount (Supabase session → fall back to the
 *      legacy cookie)
 *   2. Subscribe to Supabase auth state changes (SIGNED_IN / SIGNED_OUT)
 *   3. Auto-logout after 30 min of inactivity
 *
 * Step 1 is guarded by AUTH_INIT_TIMEOUT_MS: every call it awaits can hang
 * (auth-js waits on a cross-tab lock that a stuck tab never releases; the
 * profile query and the legacy /auth/me have no transport timeout), and a
 * hang here leaves the app on the loading screen for ever. When the guard
 * fires, the stored session is dropped so the next load starts clean and
 * the login page is shown.
 */

import { useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { supabase } from '../supabaseClient';
import logger from '../utils/logger';
import { useAuthStore, isStoreSignInInFlight } from '../stores/authStore';
import { fetchUserProfileDirect } from '../lib/userProfileDirect';
import { dropStoredSupabaseSession } from '../lib/supabaseSession';
import type { User } from '../types/models';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
export const AUTH_INIT_TIMEOUT_MS = 10 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

export class AuthInitTimeoutError extends Error {
  constructor(label: string) {
    super(`${label} did not answer within ${AUTH_INIT_TIMEOUT_MS} ms`);
    this.name = 'AuthInitTimeoutError';
  }
}

function withTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new AuthInitTimeoutError(label)), AUTH_INIT_TIMEOUT_MS);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export function useAuthInit(): void {
  const user = useAuthStore((s) => s.user);
  const authMethod = useAuthStore((s) => s.authMethod);
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);
  const setAuthMethod = useAuthStore((s) => s.setAuthMethod);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const authMethodRef = useRef<typeof authMethod>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep a ref in sync with authMethod so the Supabase subscription callback
  // can read the current value without re-subscribing on every change.
  useEffect(() => {
    authMethodRef.current = authMethod;
  }, [authMethod]);

  // 1 + 2: session restoration + Supabase subscription
  useEffect(() => {
    // Supabase session → profile (direct, then /auth/me-v2). True when a
    // user was restored; false when there is no usable session, in which
    // case the legacy cookie is tried next.
    const restoreSupabaseUser = async (): Promise<boolean> => {
      if (!supabase) return false;
      const { data: { session } } = await withTimeout(supabase.auth.getSession(), 'Supabase session lookup');
      if (!session) return false;

      const profile = await withTimeout(fetchUserProfileDirect(session.user.id), 'Profile lookup');
      if (profile) {
        setUser(profile);
        setAuthMethod('supabase');
        return true;
      }

      try {
        const res = await withTimeout(authAPI.getCurrentUserV2(), '/auth/me-v2');
        setUser(res.data as User);
        setAuthMethod('supabase');
        return true;
      } catch (error) {
        logger.error('Supabase session validation failed:', error);
        await withTimeout(supabase.auth.signOut(), 'Supabase sign-out');
        return false;
      }
    };

    const restoreLegacyUser = async (): Promise<void> => {
      try {
        const res = await withTimeout(authAPI.getCurrentUser(), '/auth/me');
        const fetched = res.data as User;
        const userData: User = { ...fetched };
        if (fetched.corporate_group_id) {
          userData.corporate_group_id = fetched.corporate_group_id;
        }
        setUser(userData);
        setAuthMethod('legacy');
      } catch (error) {
        if (error instanceof AuthInitTimeoutError) {
          logger.warn('Legacy session check timed out; staying signed out', error.message);
          return;
        }
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status !== 401 && status !== 403) {
          logger.error('Legacy JWT validation failed:', error);
        }
        // No auth → leave user null. Nothing to remove from localStorage.
      }
    };

    /** Resolve the signed-in user for a session auth-js reported (outside its lock) */
    const syncUserFromSession = async (authUserId: string) => {
      if (authMethodRef.current !== null) return;
      try {
        const profile = await withTimeout(fetchUserProfileDirect(authUserId), 'Profile lookup');
        if (profile) {
          setUser(profile);
          setAuthMethod('supabase');
          return;
        }
        const res = await withTimeout(authAPI.getCurrentUserV2(), '/auth/me-v2');
        setUser(res.data as User);
        setAuthMethod('supabase');
      } catch (error) {
        logger.error('Supabase auth state sync failed:', error);
      }
    };

    const initAuth = async () => {
      try {
        if (await restoreSupabaseUser()) return;
        // No Supabase session: show the login page now. The legacy cookie
        // check goes to the Render backend, which may be asleep (~50 s);
        // it must not hold the login form hostage. If it does find a
        // session the store gains a user and App navigates home.
        setLoading(false);
        await restoreLegacyUser();
      } catch (error) {
        // A hung or failed lookup must not keep the loading screen up. Drop
        // the stored session (without auth-js — its lock may be the very
        // thing that hung) so a reload does not hit the same wall, and let
        // the login page render.
        logger.error('Auth init did not complete; showing the login page', error);
        const removed = dropStoredSupabaseSession();
        if (removed) logger.warn('Dropped the stored Supabase session after a failed start-up', removed);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    if (!supabase) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // Every login path opens a Supabase session of its own — the
          // direct path signs in first and reads the profile from Postgres,
          // the legacy fallback adds one afterwards so RLS can resolve
          // auth.uid() (see lib/supabaseSession). In both cases the store
          // sets the user itself, so answering this event with /auth/me-v2
          // would only re-fetch what we have at the cost of a backend
          // round-trip — the one the direct path exists to avoid. The
          // in-flight flag covers the direct path, where the event lands
          // before authMethod is set; a set authMethod (either value) covers
          // everything after — the legacy fallback's later sign-in, and
          // supabase-js re-emitting SIGNED_IN on tab focus / token recovery.
          if (isStoreSignInInFlight() || authMethodRef.current !== null) return;
          // auth-js runs this callback while holding its cross-tab lock and
          // awaits it, so nothing slow may happen here: a backend round-trip
          // in this callback blocks getSession() in every tab for as long as
          // it takes (a cold Render instance: ~50 s). Leave the callback at
          // once and do the work on the next tick.
          window.setTimeout(() => {
            void syncUserFromSession(session.user.id);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          // Owns the auth state when the Supabase session is the app's
          // credential: a session revoked elsewhere (another tab's logout,
          // a failed refresh) signs this tab out too. A legacy-fallback
          // session belongs to the cookie, and logout() clears both
          // explicitly.
          if (authMethodRef.current === 'supabase') {
            clearAuth();
          }
        }
        // TOKEN_REFRESHED: no action needed; Supabase handles it.
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setLoading, setAuthMethod, clearAuth]);

  // 3: inactivity auto-logout (only while a user is signed in)
  useEffect(() => {
    if (!user) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(async () => {
        inactivityTimerRef.current = null;
        // The store's logout, not a per-method branch: the Supabase session
        // has to go too, because session restoration would otherwise sign the
        // user back in from it on the next reload without asking the backend.
        await useAuthStore.getState().logout();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const handleActivity = () => resetInactivityTimer();

    ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, handleActivity));
    resetInactivityTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, handleActivity));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [user]);
}
